type CalculateMarginInput = {
    costPrice?: number;
    currentPrice?: number;
    proposedPrice?: number;
    benchmarkMargin?: number;
    vatPercentage: number;
};

export function getExVatPrice(price: number, vatPercentage: number): number {
    return price * (100 / (100 + vatPercentage));
}

export const BELOW_COST_WARNING = 'Below Cost';

export const BELOW_COST_NEW_PRICE_ERROR =
    'New price (ex VAT) must be more than the cost price.';

export const BELOW_COST_INTERIM_PRICE_ERROR =
    'Interim price (ex VAT) must be more than the cost price.';

export const BELOW_COST_DM_APPROVAL_WARNING =
    'The proposed price is below cost (ex VAT). You may still approve this request.';

export const MAX_SAFE_PRICE = Number.MAX_SAFE_INTEGER - 1;

export function toFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number') {
        return Number.isFinite(value) && Math.abs(value) <= MAX_SAFE_PRICE
            ? value
            : null;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            return null;
        }
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) && Math.abs(parsed) <= MAX_SAFE_PRICE
            ? parsed
            : null;
    }
    return null;
}

export function isPriceBelowCostExVat(
    inclusiveVatPrice: unknown,
    costPrice: unknown,
    vatPercentage: number,
): boolean {
    const inclusiveVat = toFiniteNumber(inclusiveVatPrice);
    const cost = toFiniteNumber(costPrice);
    if (inclusiveVat == null || inclusiveVat <= 0) {
        return false;
    }
    if (cost == null || cost <= 0) {
        return false;
    }
    return getExVatPrice(inclusiveVat, vatPercentage) < cost;
}

export function calculateMargin({
    costPrice,
    currentPrice,
    proposedPrice,
    benchmarkMargin,
    vatPercentage,
}: CalculateMarginInput) {
    if (!costPrice) {
        return {
            currentMargin: 0,
            newMargin: 0,
            isMarginPositive: false,
        };
    }

    const vatMultiplier = 100 / (100 + vatPercentage);
    const currentMargin =
        (((currentPrice || 0) * vatMultiplier - costPrice) /
            ((currentPrice || 0) * vatMultiplier)) *
        100;
    const newMargin =
        (((proposedPrice || 0) * vatMultiplier - costPrice) /
            ((proposedPrice || 0) * vatMultiplier)) *
        100;

    const isMarginPositive = newMargin > (benchmarkMargin || 1);

    return {
        currentMargin,
        newMargin,
        isMarginPositive,
    };
}
